import React from 'react';

import { Chip, Stack, Typography } from '@mui/material';

import { formatDateShort } from '~/services/TimeService';

import { formatPromotionRoomScope, isPromotionAppliedToNoRooms } from '~/utils/promotionRoomScope';

import { PromotionalDiscount } from './types';

interface Props {
  index: number;
  value: PromotionalDiscount;
}

function formatDateRange(from: string | null, to: string | null): string | null {
  if (!from && !to) {
    return null;
  }
  const fromLabel = from ? formatDateShort(from) : '?';
  const toLabel = to ? formatDateShort(to) : '?';
  return `${fromLabel} - ${toLabel}`;
}

function formatRange(min: number | null | string, max: number | null | string): string | null {
  const minVal = min != null && min !== '' ? min : null;
  const maxVal = max != null && max !== '' ? max : null;
  if (minVal == null && maxVal == null) {
    return null;
  }
  return `${minVal ?? '?'}-${maxVal ?? '?'}`;
}

export default function PromotionalDiscountSummary({ index, value }: Props) {
  const discountName = value.discount_name || 'Unnamed';
  const discountPercent = Number(value.discount_percent) || 0;
  const luxPlusDiscountPercent = Number(value.luxplus_discount_percent) || 0;
  const losRange = formatRange(value.min_los, value.max_los);
  // An unscoped promotion gets no chip, matching the other optional chips.
  const roomScope = formatPromotionRoomScope(value);
  const isAppliedToNoRooms = isPromotionAppliedToNoRooms(value);
  const arrivalRange = formatRange(value.min_days_to_arrival, value.max_days_to_arrival);

  const schedule = value.schedules?.[0];
  const activePeriod = schedule ? formatDateRange(schedule.activePeriod.from, schedule.activePeriod.to) : null;
  const travelPeriod = schedule ? formatDateRange(schedule.travelPeriod.from, schedule.travelPeriod.to) : null;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mr: 1 }}>
        DISCOUNT #{index + 1}
      </Typography>
      <Chip label={discountName} size="small" color="info" variant="outlined" />
      {discountPercent > 0 && <Chip label={`Discount ${discountPercent}%`} size="small" />}
      {luxPlusDiscountPercent > 0 && <Chip label={`Lux+ ${luxPlusDiscountPercent}%`} size="small" />}
      {losRange && <Chip label={`LOS ${losRange}`} size="small" />}
      {roomScope && (
        <Chip label={roomScope} size="small" color={isAppliedToNoRooms ? 'warning' : 'default'} />
      )}
      {arrivalRange && <Chip label={`Arrival ${arrivalRange}d`} size="small" />}
      {activePeriod && <Chip label={`Active: ${activePeriod}`} size="small" />}
      {travelPeriod && <Chip label={`Travel: ${travelPeriod}`} size="small" />}
    </Stack>
  );
}
